import { FC, useEffect } from "react";
import { FormControl, InputLabel, MenuItem, Select, SxProps, Theme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getPriceListsAction } from "../../../redux/actions/priceListsAction";

export interface IPriceListSelectProps {
  value: number | null;
  onChange: (priceListId: number | null) => void;
  label?: string;
  // Adds a "Default (<name>)" option that maps to null, for customers who
  // should follow whichever list is the default
  allowFollowDefault?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  minWidth?: string;
  sx?: SxProps<Theme>;
}

const FOLLOW_DEFAULT = "follow-default";

/** Dropdown of active price lists; loads them on first use. */
const PriceListSelect: FC<IPriceListSelectProps> = ({
  value,
  onChange,
  label = "Price list",
  allowFollowDefault = false,
  disabled,
  fullWidth,
  size = "small",
  minWidth = "16rem",
  sx,
}) => {
  const dispatch = useAppDispatch();
  const { priceLists, priceListsLoading } = useAppSelector((state) => state.priceLists);

  useEffect(() => {
    if (priceLists.length === 0 && !priceListsLoading) dispatch(getPriceListsAction());
  }, []);

  const defaultList = priceLists.find((list) => list.isDefault);
  const options = priceLists.filter((list) => list.active);
  const selected =
    value == null ? (allowFollowDefault ? FOLLOW_DEFAULT : "") : String(value);

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      sx={[{ minWidth }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      disabled={disabled}
    >
      {label && <InputLabel sx={{ fontSize: "var(--fs-2xs)" }}>{label}</InputLabel>}
      <Select
        label={label || undefined}
        value={options.length === 0 ? "" : selected}
        onChange={(e) => {
          const raw = e.target.value as string;
          onChange(raw === FOLLOW_DEFAULT || raw === "" ? null : Number(raw));
        }}
        sx={{ fontSize: "var(--fs-2xs)" }}
      >
        {allowFollowDefault && (
          <MenuItem value={FOLLOW_DEFAULT} sx={{ fontSize: "var(--fs-2xs)" }}>
            Default{defaultList ? ` (${defaultList.name})` : ""}
          </MenuItem>
        )}
        {options.map((list) => (
          <MenuItem key={list.id} value={String(list.id)} sx={{ fontSize: "var(--fs-2xs)" }}>
            {list.name}
            {list.isDefault && !allowFollowDefault ? " (default)" : ""}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default PriceListSelect;
